import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { useCheckin } from '../../hooks/checkin/queries';
import { useMindfulness } from '../../hooks/mindfulness/queries';
import { useReflections } from '../../hooks/reflection/queries';
import { useUser, useUserSettings } from '../../hooks/user/queries';
import { useTranslation } from '../../hooks/useTranslation';
import { ReportDate } from '../../hooks/well-being/ReportDate';
import { userShape } from '../../shapes/userShape';
import CongratulationsNoSurvey from '../CongratulationsNoSurvey';
import Cover from '../Cover';
import EnergyLevel from '../EnergyLevel';
import ExecutiveSummary from '../ExecutiveSummary';
import GoingForward from '../GoingForward';
import MeditationsReflections from '../MeditationsReflections';
import TableOfContentsNoSurvey from '../TableOfContentsNoSurvey';
import WordCloudCategorized from '../WordCloudCategorized';

const mailchimpIdClaim = 'https://web.checkingin.co/mailchimpId';

function FilterCheckinInData(allCheckIns, allReflections, allMindfulnesses, baselineDate) {
	const baselinePlus30 = moment(baselineDate).add(31, 'days'); // 31 to be inclusive
	// console.log('baselineDate', moment(baselineDate).format('YYYYMMDD'));
	// console.log('baselinePlus30', moment(baselinePlus30).format('YYYYMMDD'));
	const checkinsFiltered = allCheckIns.filter((c) => {
		const cd = moment(c.createdDate).format('YYYYMMDD');
		// console.log('CheckingInDate', cd);
		return cd >= moment(baselineDate).format('YYYYMMDD') && cd <= moment(baselinePlus30).format('YYYYMMDD');
	});

	const checkins = checkinsFiltered.map((e) => {
		const localDate = moment(e.createdDate).local().format('YYYYMMDD');
		return { ...e, localDate };
	});

	const reflections = allReflections.filter((c) => {
		if (c.unlockDate !== null) {
			const cd = moment(c.unlockDate).format('YYYYMMDD');
			return cd >= moment(baselineDate).format('YYYYMMDD') && cd <= moment(baselinePlus30).format('YYYYMMDD');
		}
		return null;
	});

	const mindfulnesses = allMindfulnesses.filter((c) => {
		if (c.completedDate !== null) {
			const cd = moment(c.completedDate).format('YYYYMMDD');
			return cd >= moment(baselineDate).format('YYYYMMDD') && cd <= moment(baselinePlus30).format('YYYYMMDD');
		}
		return null;
	});

	return { checkins, reflections, mindfulnesses };
}

function ReportNoSurvey({ user }) {
	const { days = 30, challengeStartDate } = useParams();
	const { t, switchLanguage } = useTranslation();
	const timezone = user['https://web.checkingin.co/geoip'].time_zone;
	const defaultUserId = user[mailchimpIdClaim] ? user[mailchimpIdClaim] : null;

	const [loading, setLoading] = useState(true);
	const [userId] = useState(defaultUserId);

	const { userSettings } = useUserSettings();
	const { loading: loadingUser, data: userData, error } = useUser(timezone);
	const { loading: loadingCheckins, error: errorCheckins, data: dataCheckins } = useCheckin();
	const { loading: loadingReflections, error: errorReflections, data: dataReflections } = useReflections();
	const { loading: loadingMindfulnesses, error: errorMindfulnesses, data: dataMindfulnesses } = useMindfulness();

	const loadUserLanguage = useCallback(() => {
		if (userSettings) {
			const res = userSettings.find((setting) => setting.type === 'Preferences');
			if (res && res.meta && Array.isArray(res.meta)) {
				const userLanguage = res.meta.find((data) => data.name === 'language');
				if (userLanguage) {
					const selectedLanguage = userLanguage.value === 'fr' ? 'fr-CA' : 'en-US';
					switchLanguage(selectedLanguage);
				}
			}
		}
	}, [userSettings, switchLanguage]);

	useEffect(() => {
		loadUserLanguage();
	}, [loadUserLanguage]);

	useEffect(() => {
		const loadingAllData = loadingUser || loadingCheckins || loadingReflections || loadingMindfulnesses;
		// setTimeout Just to make sure we load all data
		setTimeout(setLoading(loadingAllData), 100);
	}, [loadingUser, loadingCheckins, loadingReflections, loadingMindfulnesses]);

	const today = new Date(); // This will be the Exit createdAt

	if (error) {
		console.log('Error: ', error);
		return <div>{t('components:other.somethingWentWrong')}</div>;
	}

	if (errorCheckins || errorReflections || errorMindfulnesses) {
		console.log('errorCheckins', errorCheckins);
		console.log('errorReflections', errorReflections);
		console.log('errorMindfulnesses', errorMindfulnesses);
		return <div>Uh oh!</div>;
	}

	if (loading || !userData || !dataCheckins || !dataReflections || !dataMindfulnesses) {
		return <Spinner />;
	}

	// Get Executive Summary
	const { checkIns: allCheckIns } = dataCheckins;
	const { reflections: allReflections } = dataReflections;
	const { mindfulnesses: allMindfulnesses } = dataMindfulnesses;

	// Here we have an important rule for this report
	// If challengeStartDate is passed we must use it as a base for the report
	// otherwise we'll get now - the number of days of the report (the extra -1 is to account for midnight of today so it is inclusive)
	const baselineDate = ReportDate(challengeStartDate ? moment(challengeStartDate) : moment().subtract(Number(days) - 1, 'days'));

	const filteredData = FilterCheckinInData(allCheckIns, allReflections, allMindfulnesses, baselineDate.originalDate);

	const data = {
		date: ReportDate(today), // Month day, year
		user: { ...userData, userId },
		survey: {
			baseline: {
				date: baselineDate,
			},
		},
		...filteredData,
	};

	if (!data.checkins || !data.reflections || !data.mindfulnesses) {
		return <Spinner />;
	}

	return (
		<div className="report">
			<main>
				<Cover data={data} />
				<CongratulationsNoSurvey data={data} pageNumber={1} days={days} />
				<TableOfContentsNoSurvey pageNumber={2} days={days} />
				<ExecutiveSummary data={data} pageNumber={3} days={days} />
				<WordCloudCategorized data={data} pageNumber={4} days={days} />
				<EnergyLevel data={data} pageNumber={5} days={days} />
				<MeditationsReflections data={data} pageNumber={6} days={days} />
				<GoingForward pageNumber={7} days={days} />
			</main>
		</div>
	);
}

ReportNoSurvey.propTypes = {
	user: userShape.isRequired,
};

export default ReportNoSurvey;
